import React, { useContext } from "react";
import { BNBox, dateFormatter, numberFormatter } from "@bn-monorepo/js-packages";
import { Typography } from "@mui/material";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarHalfRoundedIcon from "@mui/icons-material/StarHalfRounded";
import StarOutlineRoundedIcon from "@mui/icons-material/StarOutlineRounded";
// import GoogleLogo from "./google-logo.svg"; // logo removed from the design, kept for possible future use
import { SettingsContext } from "../../../settingsContext";
import "./style.scss";

interface ReviewSummary {
  rating: number;
  reviewCount: number;
  reviewsUrl?: string;
  source?: string;
}

interface Props {
  content?: {
    alias?: string;
    text?: string;
    name?: string;
    reviewSummary?: ReviewSummary;
  };
}

const GoogleReview: ReactFCWithNoJs<Props> = ({ content }) => {
  const { locale } = useContext(SettingsContext);
  const summary = content?.reviewSummary;
  if (!summary) return null;

  const rating = Number(summary.rating || 0);
  const reviewCount = summary.reviewCount;
  const formattedToday = dateFormatter(locale)(new Date());
  const formattedRating = rating.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const formattedReviewCount = numberFormatter(locale)(reviewCount);

  const mergeFields = (text?: string) =>
    text?.replace("{reviewCount}", formattedReviewCount).replace("{date}", formattedToday) ?? "";

  const roundedRating = Math.round(rating * 2) / 2;
  const fullStars = Math.floor(roundedRating);
  const hasHalfStar = roundedRating % 1 !== 0;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  const renderStars = () => (
    <div className="google-review__stars" aria-label={`${rating.toFixed(1)} out of 5`}>
      {Array.from({ length: fullStars }).map((_, i) => (
        <StarRoundedIcon key={`full-${i}`} />
      ))}
      {hasHalfStar && <StarHalfRoundedIcon key="half" />}
      {Array.from({ length: emptyStars }).map((_, i) => (
        <StarOutlineRoundedIcon key={`empty-${i}`} />
      ))}
    </div>
  );

  const reviewContent = content?.text ? (
    <div className="google-review google-review--card">
      <div className="google-review__top">
        {renderStars()}
        <Typography
          component="span"
          className="google-review__rating"
          sx={{
            fontWeight: "bold",
          }}
        >
          {formattedRating} {content?.name} 5
        </Typography>
      </div>

      <Typography component="div" className="google-review__label">
        {mergeFields(content.text)}
      </Typography>
    </div>
  ) : (
    <div className="google-review google-review--compact">
      <Typography
        component="span"
        className="google-review__rating"
        sx={{
          fontWeight: "bold",
        }}
      >
        {formattedRating} {content?.name} 5
      </Typography>

      {renderStars()}

      <Typography component="span" className="google-review__count">
        ({formattedReviewCount} reviews)
      </Typography>
    </div>
  );

  return <BNBox className="google-review-wrapper bncms-component">{reviewContent}</BNBox>;
};

GoogleReview.noJsRequired = true;
export default GoogleReview;
